// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {useArboriumHighlightedHtml} from '@app/features/code_highlighting/utils/ArboriumHighlighting';
import {Logger} from '@app/features/platform/utils/AppLogger';
import styles from '@app/features/theme/components/modals/ThemeAcceptModal.module.css';
import {buildThemeCssProxyUrl} from '@app/features/theme/utils/ThemeUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Trans, useLingui} from '@lingui/react/macro';
import {CheckCircleIcon, ClipboardIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const logger = new Logger('ThemeAcceptModal');
interface ThemeAcceptModalProps {
	themeId: string;
}
export const ThemeAcceptModal = observer(function ThemeAcceptModal({themeId}: ThemeAcceptModalProps) {
	const {t, i18n} = useLingui();
	const [isApplying, setIsApplying] = useState(false);
	const [isCopied, setIsCopied] = useState(false);
	const [css, setCss] = useState<string | null>(null);
	const [fetchStatus, setFetchStatus] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
	const [fetchError, setFetchError] = useState<string | null>(null);
	const mediaEndpoint = RuntimeConfig.mediaEndpoint;
	const highlightedCss = useArboriumHighlightedHtml('css', css);
	useEffect(() => {
		if (!mediaEndpoint) {
			setCss(null);
			setFetchStatus('idle');
			setFetchError(null);
			return;
		}
		let cancelled = false;
		const fetchTheme = async () => {
			setFetchStatus('loading');
			setFetchError(null);
			try {
				const themeUrl = buildThemeCssProxyUrl(mediaEndpoint, themeId);
				if (!themeUrl) {
					throw new Error('Media endpoint not configured');
				}
				const response = await fetch(themeUrl);
				if (!response.ok) {
					throw new Error('Theme not found');
				}
				const text = await response.text();
				if (cancelled) return;
				setCss(text);
				setFetchStatus('ready');
			} catch (error) {
				if (cancelled) return;
				logger.error('Failed to fetch theme:', error);
				setCss(null);
				setFetchStatus('error');
				setFetchError(t`We couldn't read this theme. It may be corrupted or invalid.`);
			}
		};
		void fetchTheme();
		return () => {
			cancelled = true;
		};
	}, [mediaEndpoint, themeId]);
	const handleDismiss = () => {
		ModalCommands.pop();
	};
	const handleCopy = () => {
		if (!css) return;
		TextCopyCommands.copy(i18n, css);
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};
	const handleApply = async () => {
		if (!css) {
			ToastCommands.error(fetchError ?? t`This theme is still loading.`);
			return;
		}
		setIsApplying(true);
		try {
			AccessibilityCommands.update({customThemeCss: css});
			ToastCommands.success(t`Theme applied successfully.`);
			ModalCommands.pop();
		} catch (error) {
			logger.error('Failed to apply theme:', error);
			ToastCommands.error(t`We couldn't apply this theme.`);
			setIsApplying(false);
		}
	};
	const renderCodeContent = () => {
		if (fetchStatus === 'loading') {
			return (
				<span className={styles.loadingText}>
					<Trans>Loading theme...</Trans>
				</span>
			);
		}
		if (fetchStatus === 'error') {
			return <span className={styles.errorText}>{fetchError}</span>;
		}
		if (!css) {
			return null;
		}
		return <code className={styles.hljs} dangerouslySetInnerHTML={{__html: highlightedCss}} />;
	};
	return (
		<Modal.Root size="medium">
			<Modal.Header title={<Trans>Import Theme</Trans>} />
			<Modal.Content padding="none" className={styles.content}>
				<p className={styles.description}>
					<Trans>This will replace your current custom theme. You can edit it later in your User Settings.</Trans>
				</p>
				<div className={styles.codeContainer}>
					<div className={clsx(styles.codeActions, isCopied && styles.codeActionsVisible)}>
						<button
							type="button"
							onClick={handleCopy}
							className={styles.copyButton}
							aria-label={isCopied ? t`Copied!` : t`Copy code`}
							disabled={!css}
						>
							{isCopied ? (
								<CheckCircleIcon className={styles.copyIcon} />
							) : (
								<ClipboardIcon className={styles.copyIcon} />
							)}
						</button>
					</div>
					<pre className={styles.pre}>{renderCodeContent()}</pre>
				</div>
			</Modal.Content>
			<Modal.Footer className={styles.footer}>
				<Button variant="secondary" onClick={handleDismiss} disabled={isApplying}>
					<Trans>Cancel</Trans>
				</Button>
				<Button onClick={handleApply} disabled={isApplying || fetchStatus !== 'ready'} submitting={isApplying}>
					<Trans>Apply</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
